import type { SessionData } from '@app/constants/StoreTypes'

import type { CookieInterface } from '@app/services/CookieManager'

import { deleteCookie } from '@app/packages/internalApi/actions/cookie'
import { deleteSession, postSession } from '@app/packages/internalApi/actions/session'
import { unwrapOr } from '@app/packages/Result/Result'

import { callInternalApi } from '@app/store/actions/api'
import { createThunk } from '@app/store/thunk'

import { accessTokenHash } from './accessTokenHash'
import { createCookieName } from './cookieNames'
import { createCookie } from './cookies'
import { parseCookieHeader } from './cookies/parseCookieHeader'
import { getJwtExp } from './jwt'
import moment from './moment'
import { tryJSONparse } from './tryJSONparse'
import { valita } from './valita'
import { wrapError } from './wrapError'

/** Refresh-token cookie name; the backend sets it. Exported because `serverAuthSession` reads it directly off the ctx. */
export const REFRESH_TOKEN_COOKIE = createCookieName('kidsout_rt')

// MARK: Legacy session cookie (kidsout_session)

/**
 * Legacy session cookie name. Exported so the SSR layer can also clear a stray
 * host-only copy left over from before `COOKIE_DOMAIN` existed (see
 * `clearHostOnlyCookie`).
 */
export const SESSION_COOKIE = createCookieName('kidsout_session')

export function clearSession(cookies: CookieInterface) {
  return sessionCookie.rm(cookies)
}

export async function persistSession(cookies: CookieInterface, headers: Partial<SessionData> | null, ignoreConsent: boolean) {
  if (!headers) {
    await clearSession(cookies)
    return
  }

  const oldval = await sessionCookie.get(cookies)
  const newval = { ...oldval, ...headers }
  if (newval.access_token) await setSession(cookies, newval as SessionData, ignoreConsent)
}

export function extractSession(cookies: CookieInterface) {
  return sessionCookie.get(cookies)
}

// MARK: JWT access-token cookie (kidsout__at) + readable meta companion (kidsout__at_meta)

export type AccessTokenCookieValue = valita.Infer<typeof accessTokenCookieSchema>

/**
 * Non-httpOnly companion to `kidsout__at`, carrying only the subject id and a
 * hash of the access token — never the token itself. Because it is readable from
 * `document.cookie`, client JS can synchronously tell whether the in-memory token
 * is still current (e.g. on a bfcache restore) without exposing the JWT.
 */
export type AccessTokenMetaCookieValue = valita.Infer<typeof accessTokenMetaCookieSchema>

export type SessionCookieSpec = { name: string; value: string; httpOnly: boolean; expiresAt: number }

/**
 * The cookie pair that represents a v3 session: the httpOnly access token and
 * its readable meta companion, which always share the same expiry. Single source
 * of truth for both the SSR `CookieInterface` writer and the internal `/session`
 * endpoint, so the two cookies can never carry inconsistent values or lifetimes.
 */
export const accessTokenCookieSpecs = (value: AccessTokenCookieValue): SessionCookieSpec[] => [
  { name: ACCESS_TOKEN_COOKIE, value: encodeURIComponent(JSON.stringify(value)), httpOnly: true, expiresAt: value.expires_at },
  { name: AT_META_COOKIE, value: encodeURIComponent(JSON.stringify(toAccessTokenMeta(value))), httpOnly: false, expiresAt: value.expires_at },
]

/** The session cookie-pair names, for clearing both at once. */
export const sessionCookieNames = (): readonly string[] => [ACCESS_TOKEN_COOKIE, AT_META_COOKIE]

export const writeAccessTokenCookieToCtx = async (cookies: CookieInterface, value: AccessTokenCookieValue): Promise<void> => {
  await Promise.all(
    accessTokenCookieSpecs(value).map(spec =>
      cookies.set(spec.name, spec.value, { httpOnly: spec.httpOnly, sameSite: 'lax', expires: moment(spec.expiresAt), ignoreConsent: true })
    )
  )
}

export const clearAccessTokenCookieToCtx = async (cookies: CookieInterface): Promise<void> => {
  await Promise.all(sessionCookieNames().map(name => cookies.remove(name)))
}

export const readAccessTokenCookie = async (cookies: CookieInterface): Promise<AccessTokenCookieValue | null> => {
  const raw = await cookies.get(ACCESS_TOKEN_COOKIE)
  if (!raw) return null
  try {
    return accessTokenCookieSchema.parse(JSON.parse(decodeURIComponent(raw)), { mode: 'strip' })
  } catch {
    return null
  }
}

/** Synchronous client-side read of the meta companion from `document.cookie`. */
export const readAccessTokenMeta = (): AccessTokenMetaCookieValue | null => {
  if (!IS_BROWSER) return null
  const raw = parseCookieHeader(document.cookie).get(AT_META_COOKIE)
  if (!raw) return null
  try {
    return accessTokenMetaCookieSchema.parse(JSON.parse(decodeURIComponent(raw)), { mode: 'strip' })
  } catch {
    return null
  }
}

/**
 * Server-side read of the meta companion from a `CookieInterface` (the
 * `document.cookie` counterpart is `readAccessTokenMeta`). Returns null when the
 * marker is absent or unparseable — both cases warrant a backfill.
 */
export const readAccessTokenMetaCookie = async (cookies: CookieInterface): Promise<AccessTokenMetaCookieValue | null> => {
  const raw = await cookies.get(AT_META_COOKIE)
  if (!raw) return null
  try {
    return accessTokenMetaCookieSchema.parse(JSON.parse(decodeURIComponent(raw)), { mode: 'strip' })
  } catch {
    return null
  }
}

/** Writes the session cookie pair atomically via the internal `/session` endpoint. */
export const writeAccessTokenCookieFromClient = (value: AccessTokenCookieValue) =>
  createThunk(async dispatch => {
    try {
      await dispatch(callInternalApi(postSession({ user_id: value.user_id, access_token: value.access_token })))
    } catch {
      // Best-effort cookie sync
    }
  })

export const clearAccessTokenCookieFromClient = () =>
  createThunk(async dispatch => {
    try {
      await dispatch(callInternalApi(deleteSession()))
    } catch {
      // Best-effort cookie sync
    }
  })

// MARK: Refresh-token cookie (kidsout_rt)

/**
 * Clears the refresh cookie via the SSR server (it is httpOnly and scoped to
 * COOKIE_DOMAIN, so the browser can't drop it directly). The server applies the
 * domain automatically. Awaiting this before a logout reload is essential —
 * otherwise the auth manager would just refresh the session straight back.
 */
export const clearRefreshTokenCookieFromClient = () =>
  createThunk(async dispatch => {
    try {
      await dispatch(callInternalApi(deleteCookie({ name: REFRESH_TOKEN_COOKIE, options: { path: '/' } })))
    } catch {
      // Best-effort cookie sync
    }
  })

// MARK: Supervision cookie (kidsout__supervision)

/** Persists the supervision (impersonation) JWT; the cookie expires with the token. */
export const writeSupervisionCookieToCtx = (cookies: CookieInterface, token: string): Promise<void> => {
  const exp = getJwtExp(token)
  return cookies.set(SUPERVISION_COOKIE, token, {
    httpOnly: true,
    sameSite: 'lax',
    ignoreConsent: true,
    ...(exp ? { expires: moment(exp) } : {}),
  })
}

export const clearSupervisionCookieToCtx = (cookies: CookieInterface): Promise<void> => cookies.remove(SUPERVISION_COOKIE)

export const readSupervisionCookie = async (cookies: CookieInterface): Promise<string | null> => (await cookies.get(SUPERVISION_COOKIE)) || null

export const clearSupervisionCookieFromClient = () =>
  createThunk(async dispatch => {
    try {
      await dispatch(callInternalApi(deleteCookie({ name: SUPERVISION_COOKIE, options: { path: '/' } })))
    } catch {
      // Best-effort cookie sync
    }
  })

// MARK: Reset every auth cookie

/**
 * Clears every auth cookie: the legacy session, the JWT access cookie, the
 * refresh cookie, and the supervision cookie. Clearing `kidsout_rt` is essential
 * — otherwise the auth manager would just refresh the session straight back.
 */
export const resetAuthCookies = async (cookies: CookieInterface): Promise<void> => {
  await Promise.all([clearSession(cookies), clearAccessTokenCookieToCtx(cookies), clearSupervisionCookieToCtx(cookies), cookies.remove(REFRESH_TOKEN_COOKIE)])
}

// MARK: Internal

const ACCESS_TOKEN_COOKIE = createCookieName('kidsout__at')
const AT_META_COOKIE = createCookieName('kidsout__at_meta')
const SUPERVISION_COOKIE = createCookieName('kidsout__supervision')

const accessTokenCookieSchema = valita.object({
  access_token: valita.string(),
  expires_at: valita.number(),
  user_id: valita.string(),
})

const accessTokenMetaCookieSchema = valita.object({
  user_id: valita.string(),
  hash: valita.string(),
})

const toAccessTokenMeta = (value: AccessTokenCookieValue): AccessTokenMetaCookieValue => ({
  user_id: value.user_id,
  hash: accessTokenHash(value.access_token),
})

const sessionCookie = createCookie<SessionData | null>(
  SESSION_COOKIE,
  v => {
    const data = unwrapOr(tryJSONparse<SessionData | null>(v), null)
    if (!data) return data
    if (!data.access_token) return null
    return data
  },
  v => JSON.stringify(v),
  () => ({ expires: moment().add(10, 'years'), sameSite: 'lax' })
)

async function setSession(cookies: CookieInterface, value: SessionData, ignoreConsent: boolean) {
  try {
    await sessionCookie.set(cookies, value, ignoreConsent)
  } catch (e) {
    throw wrapError(new Error('Cannot set session cookie'), e)
  }
}
